import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HomeService } from 'src/home/home.service';
import {
  ActivityParams,
  ActivityLogResponse,
} from 'src/models/activity.interface';
import { getEventDescription } from 'src/models/eventDescriptions';
import Swal from 'sweetalert2';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { PaginatorService } from 'src/home/paginator.service';
import { BillingService } from 'src/home/billing/services/billing.service';

@Component({
  selector: 'app-activity-log',
  templateUrl: './activity-log.component.html',
  styleUrls: ['./activity-log.component.css'],
  providers: [{ provide: MatPaginatorIntl, useClass: PaginatorService }],
})
export class ActivityLogComponent implements OnInit {
  listEventRegistry: ActivityLogResponse = {
    pageNumber: 0,
    totalPages: 0,
    currentPageElements: 0,
    totalElements: 0,
    results: [],
  };
  filterForm: FormGroup;
  isLoading: boolean = false;
  showAdvancedOptions: boolean = false;
  activities: ActivityParams[] = [];
  itemsPerPage: number = 10;
  currentPage: number = 1;
  public totalItems: number = 0;

  constructor(
    private fb: FormBuilder,
    private billingService: BillingService,
    private dialog: MatDialog
  ) {
    this.filterForm = this.fb.group({
      provider_id: [''],
      billing_start_date: [''],
      billing_end_date: [''],
      log: [''],
      type: [''],
      date: [''],
      event: [null],
      page: [1],
      count: [10],
    });
  }

  ngOnInit(): void {
    this.handleLoadActivityLogs();
    this.setupFilterFormSubscriptions();
  }

  setupFilterFormSubscriptions(): void {
    this.filterForm.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.applyFilters();
      });
  }

  handleLoadActivityLogs(): void {
    this.isLoading = true;
    let formValues = this.filterForm.getRawValue();
    this.billingService.getBillingEvents(formValues).subscribe(
      (response: ActivityLogResponse) => {
        this.isLoading = false;
        this.listEventRegistry = response;
        this.activities = response.results.map(activity => ({
          ...activity,
          descriptionAsArray: Array.isArray(activity.descriptionAsArray) ? activity.descriptionAsArray : []
        }));
        this.totalItems = response.totalElements ?? 0;
        this.currentPage = response.pageNumber ?? 1;
      },
      (error) => {
        console.error('Error al cargar los registros de actividad:', error);
        this.isLoading = false;
        Swal.fire({
          title: 'Error',
          text: 'Ha ocurrido un error al cargar los registros de actividad. Por favor, inténtalo de nuevo.',
          icon: 'error',
          confirmButtonColor: '#d33',
        });
      }
    );
  }

  handlePageEvent(event: any): void {
    const pageIndex = event.pageIndex ?? 0;
    const pageSize = event.pageSize ?? this.itemsPerPage;

    this.filterForm.patchValue({
      page: pageIndex + 1,
      count: pageSize,
    });
    this.applyFilters();
  }

  applyFilters(): void {
    this.isLoading = true;
    this.handleLoadActivityLogs();
  }

  clearFilters(): void {
    this.filterForm.reset({
      page: 1,
      count: 10,
    });
    this.applyFilters();
  }

  toggleAdvancedOptions(): void {
    this.showAdvancedOptions = !this.showAdvancedOptions;
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${this.padNumber(
      date.getMonth() + 1
    )}-${this.padNumber(date.getDate())} ${this.padNumber(
      date.getHours()
    )}:${this.padNumber(date.getMinutes())}:${this.padNumber(
      date.getSeconds()
    )}`;
  }

  padNumber(num: number): string {
    return num < 10 ? `0${num}` : num.toString();
  }

  getEventDescriptionWrapper(eventCode: string): string {
    return getEventDescription(eventCode);
  }

  pageChanged(newPage: number): void {
    this.filterForm.patchValue({ page: newPage });
    this.applyFilters();
  }

  getIndex(index: number): number {
    return (
      this.totalItems - ((this.currentPage - 1) * this.itemsPerPage + index)
    );
  }
}
